/**
 * Codex Design System — Theme
 *
 * Factory for the Codex MUI theme used across the Vellum app.
 *
 * Quick start:
 *   import { getCodexTheme } from '@/theme';
 *   const theme = getCodexTheme('light');
 *   <ThemeProvider theme={theme}><CssBaseline /><App /></ThemeProvider>
 */

import { createTheme, type PaletteMode, type CSSObject } from '@mui/material/styles';

import { typography, displayVariant } from './tokens/typography';
import { spacingBase, radii } from './tokens/spacing';
import { durations, easings } from './tokens/motion';
import { shadowLight, shadowDark, buildMuiShadows } from './tokens/elevation';
import { lightColors } from './semantic/light';
import { darkColors } from './semantic/dark';
import { buildPalette } from './builders/palette';
import { buildComponents } from './builders/components';
import { breakpoints } from './breakpoints';

// ── Theme factory ──────────────────────────────────────────────────────────

export function getCodexTheme(mode: PaletteMode = 'light') {
  const colors = mode === 'dark' ? darkColors : lightColors;
  const shadows = mode === 'dark' ? shadowDark : shadowLight;

  return createTheme({
    palette: buildPalette(colors),
    typography: { ...typography, display: displayVariant },
    breakpoints,
    spacing: spacingBase,
    shape: { borderRadius: radii.md },
    shadows: buildMuiShadows(shadows),
    transitions: { duration: durations, easing: easings },
    components: buildComponents(colors, shadows),
  });
}

/** Pre-built light theme instance */
export const codexLight = getCodexTheme('light');
/** Pre-built dark theme instance */
export const codexDark = getCodexTheme('dark');

export default getCodexTheme;

// ── Token re-exports ───────────────────────────────────────────────────────
export * from './tokens/index';
export * from './semantic/index';
export { breakpoints } from './breakpoints';

// ── TypeScript augmentations ───────────────────────────────────────────────
declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    sxs: true;
    xxl: true;
  }
  interface TypographyVariants {
    display: CSSObject;
  }
  interface TypographyVariantsOptions {
    display?: CSSObject;
  }
  interface TypeBackground {
    body?: string;
    dark?: string;
    light?: string;
  }
  interface TypeBackgroundOptions {
    body?: string;
    dark?: string;
    light?: string;
  }
}
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    display: true;
  }
}
